/**
 * SealedKeyStore wraps an arbitrary `KeyStore` so that every value written is
 * encrypted under a 32-byte symmetric key (typically derived from a passphrase
 * via `deriveKey`). The underlying store only ever sees ciphertext.
 *
 * The plaintext key name is bound as associated data — a server or attacker
 * that swaps values between slots cannot silently substitute them.
 */
import type { KeyStore } from './keystore.js';
import { open, seal } from './passphrase.js';

export class SealedKeyStore implements KeyStore {
  readonly #inner: KeyStore;
  readonly #key: Uint8Array;

  constructor(inner: KeyStore, rawKey: Uint8Array) {
    if (rawKey.length !== 32) {
      throw new Error('sealing key must be 32 bytes');
    }
    this.#inner = inner;
    this.#key = new Uint8Array(rawKey);
  }

  async get(key: string): Promise<Uint8Array | undefined> {
    const sealed = await this.#inner.get(key);
    if (sealed === undefined) return undefined;
    return open(this.#key, sealed, new TextEncoder().encode(key));
  }

  async put(key: string, value: Uint8Array): Promise<void> {
    const sealed = await seal(this.#key, value, new TextEncoder().encode(key));
    await this.#inner.put(key, sealed);
  }

  delete(key: string): Promise<void> {
    return this.#inner.delete(key);
  }

  list(prefix?: string): Promise<readonly string[]> {
    return this.#inner.list(prefix);
  }
}
